import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { Product, Quotes } from '@models/models';
import { TimeServiceProvider } from '@services/helpers/time-service/time.service';
import { ReplaySubject } from 'rxjs';

@Injectable()
export class QuoteUpdateService {
	updatedProduct$: BehaviorSubject<any> = new BehaviorSubject(null);
	updatePaymentsScheduleSubject: BehaviorSubject<any> = new BehaviorSubject(
		null
	);
	updatePoNumberSubject: BehaviorSubject<string | null> = new BehaviorSubject(
		null
	);
	updateIntroductionSubject: BehaviorSubject<
		string | null
	> = new BehaviorSubject(null);
	updateNotesSubject: BehaviorSubject<string | null> = new BehaviorSubject(
		null
	);

	constructor(private timeService: TimeServiceProvider) {}

	static getPageRequest() {
		return {
			filterBy: 'all',
			paging: {
				pageNumber: 0,
				pageSize: 1000,
				searchToken: null
			},

			sortCriterias: [
				{
					fieldName: 'id',
					sortAscending: true
				}
			]
		};
	}

	get updatePoNumberObservable(): Observable<string> {
		return this.updatePoNumberSubject.asObservable();
	}

	static setPackagesAndAddons(products) {
		return {
			packages: products.filter(
				(product): boolean => product.type === 'PACKAGE'
			),
			addons: products.filter((product): boolean => product.type === 'ADD_ON'),
			discounts: products.filter((product): boolean => product.type === 'GIFT')
		};
	}

	static separateDiscountsAndProducts(products) {
		return {
			addons: products.filter(
				(product): boolean =>
					product.type === 'PACKAGE' || product.type === 'ADD_ON'
			),
			discounts: products.filter((product): boolean => product.type === 'GIFT')
		};
	}

	static setFixedQuoteProducts(products) {
		return products.map(product => {
			if (product.type === 'PACKAGE') {
				product.type = 'ADD_ON';
			}
			return product;
		});
	}

	static setDiscount(products: Array<any> = []) {
		if (products.length > 0 && products.length !== 1) {
			const discount = this.setEmptyProduct('GIFT');
			let sum = 0;
			products.map(product => {
				sum += Math.abs(product.amount);
			});
			return Object.assign(discount, {
				amount: -sum,
				taxType: 'NO_TAX',
				mandatory: true,
				name: 'Gift Certificate',
				description: '<p>Fixed fee discount</p>',
				price: -sum,
				summary: {
					subTotal: -sum,
					total: -sum
				}
			});
		} else if (products.length === 1) {
			return products[0];
		}

		return null;
	}

	get updateIntroductionObservable(): Observable<string> {
		return this.updateIntroductionSubject.asObservable();
	}

	get updateNotesObservable(): Observable<string> {
		return this.updateNotesSubject.asObservable();
	}

	get updatePaymentScheduleObservable(): Observable<string> {
		return this.updatePaymentsScheduleSubject.asObservable();
	}

	static setEmptyProduct(type: string): Product {
		return {
			id: null,
			chosen: false,
			description: '',
			name: '',
			price: 0,
			quantity: 1,
			discount: 0,
			total: 0,
			discountValue: 0,
			summary: {
				total: 0
			},
			order: 1,
			taxType: 'INCLUDED',
			taxValue: 0,
			recommended: false,
			mandatory: type === 'GIFT',
			type
		};
	}

	static setDiscountTypes() {
		return ['Fixed', 'Percent of order'];
	}

	static getDefaultDiscount(discountAmount) {
		const discount = this.setEmptyProduct('GIFT');
		return Object.assign(discount, {
			amount: discountAmount,
			taxType: 'NO_TAX',
			mandatory: true,
			name: 'Gift Certificate',
			description: '<p>Fixed fee discount</p>',
			price: discountAmount,
			summary: {
				subTotal: discountAmount,
				total: discountAmount
			}
		});
	}

	emitPaymentScheduleChanges(value: string): void {
		return this.updatePaymentsScheduleSubject.next(value);
	}

	emitPoNumberChanges(value: string): void {
		return this.updatePoNumberSubject.next(value);
	}

	emitIntroductionChanges(value: string): void {
		return this.updateIntroductionSubject.next(value);
	}

	emitNotesChanges(value: string): void {
		return this.updateNotesSubject.next(value);
	}

	getNow(): string {
		return this.timeService.getDateString(new Date());
	}

	getDate(date): string {
		return this.timeService.getDateString(date);
	}

	getNowTextFormat(): Date {
		return this.timeService.getProperDate(new Date());
	}

	getProperDate(date) {
		return this.timeService.getProperDate(date);
	}

	setQuoteIssueDate(issueDate: string) {
		return {
			jsdate: this.timeService.createDateAsUTC(issueDate)
		};
	}

	setEmptyQuote(quoteId): Quotes {
		return {
			contract: null,
			introduction: '',
			issueDate: this.getNow(),
			notes: '',
			paymentsSchedule: null,
			payments: [],
			ponumber: null,
			products: null,
			questionnaire: null,
			quoteId,
			sentOn: null,
			status: 'NOT_SENT'
		};
	}

	sortPaymentsByDate(payments) {
		return payments.sort(this.sortByDateFunction());
	}

	sortByDateFunction() {
		return (a, b) => {
			const dateOfA = a.date ? a.date.jsdate : '';
			const dateOfB = b.date ? b.date.jsdate : '';
			if (dateOfA === '') {
				return 1;
			} else if (dateOfB === '') {
				return -1;
			} else if (dateOfA === dateOfB) {
				return 0;
			}
			return dateOfA < dateOfB ? -1 : 1;
		};
	}

	validateProductsPropsDefined(products: Array<Product>): boolean {
		return products.every(
			product =>
				// should be non strict comparison to check if props are defined
				product.price != null &&
				product.quantity != null &&
				product.discount != null
		);
	}

	setNextUpdateProduct(index, product): void {
		if (index === null && product === null) {
			this.updatedProduct$.next(null);
		} else {
			this.updatedProduct$.next({ index, product });
		}
	}

	get updatedProductObservable() {
		return this.updatedProduct$.asObservable();
	}

	static sortProducts(products) {
		return products.sort((a, b) => a.order - b.order);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=kkml25e9zf!./shared/services/quote/quote-update.service.ts